import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { Loader2, Pencil, Trash2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useIsAdmin } from "@/hooks/useCurrentUser";
import {
  settingsKeys,
  useCreateUser,
  useDeleteUser,
  useUpdateUser,
  useUserRoles,
  useUsers,
  useBusinesses,
  useMeetingTypes,
  useProfileSearch
} from "@/services/settings";
import { cn } from "@/utils/cn";
import {
  BusinessMeetingTypeSelector,
  type BusinessMeetingTypeMappingRow
} from "./BusinessMeetingTypeSelector";
import { EmailLookupAutocomplete } from "./EmailLookupAutocomplete";
import type { UserResponse } from "@/types/settings";

const schema = z.object({
  userName: z.string().min(1, "User name is required"),
  email: z.string().email("Valid email is required"),
  firstName: z.string(),
  lastName: z.string(),
  // Rejects empty string AND the placeholder "0" value
  roleId: z.string().refine((v) => v !== "" && v !== "0", "Role is required")
});

type FormValues = z.infer<typeof schema>;

function displayDate(value: string | null) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM-dd-yyyy");
  } catch {
    return "-";
  }
}

export function UserManagementPage() {
  const [editing, setEditing] = useState<UserResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [mappings, setMappings] = useState<BusinessMeetingTypeMappingRow[]>([]);
  const [emailLookupText, setEmailLookupText] = useState("");
  const [businessError, setBusinessError] = useState<string>("");

  const queryClient = useQueryClient();
  // API-driven permission check: derives admin status from the user permission API response
  const isUserAdmin = useIsAdmin();
  const { data: users, isPending, isFetching } = useUsers();
  const { data: roles = [] } = useUserRoles();
  const { data: businesses = [] } = useBusinesses();
  const { data: meetingTypes = [] } = useMeetingTypes();
  const { data: profileSuggestions = [], isFetching: searchingProfiles } =
    useProfileSearch(editing?.id === 0 ? emailLookupText : "");
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      userName: "",
      email: "",
      firstName: "",
      lastName: "",
      roleId: ""
    }
  });

  // Mirrors Blazor ShouldShowBusinessSection:
  // only show for "Business Approval" / "Business Approver" / "Business Reviewer" roles
  const watchedRoleId = form.watch("roleId");
  const showBusinessSection = useMemo(() => {
    if (!watchedRoleId) return false;
    const rid = parseInt(watchedRoleId, 10);
    const found = roles.find((r) => r.id === rid);
    return (
      found?.role === "Business Approval" ||
      found?.role === "Business Approver" ||
      found?.role === "Business Reviewer"
    );
  }, [watchedRoleId, roles]);

  useEffect(() => {
    const isAddMode = editing?.id === 0;
    if (!isAddMode || !showBusinessSection || meetingTypes.length === 0 || mappings.length === 0) {
      return;
    }

    setMappings((prev) => {
      const needsDefault = prev.some((row) => row.meetingTypes.length === 0);
      if (!needsDefault) return prev;
      return prev.map((row) =>
        row.meetingTypes.length === 0 ? { ...row, meetingTypes: [...meetingTypes] } : row
      );
    });
  }, [editing?.id, showBusinessSection, meetingTypes, mappings.length]);

  const filteredUsers = useMemo(() => {
    const all = users ?? [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return all;

    return all.filter((user) => {
      const userName = user.userName?.toLowerCase() ?? "";
      const email = user.email?.toLowerCase() ?? "";
      return userName.includes(q) || email.includes(q);
    });
  }, [searchTerm, users]);

  const openCreate = () => {
    setEditing({
      id: 0,
      isLoggedInUser: false,
      email: "",
      name: "",
      firstName: "",
      lastName: "",
      userName: "",
      roleId: null,
      roleName: "",
      createdDate: null,
      lastLoginDate: null,
      businessNameCSV: "",
      meetingTypesNameCSV: "",
      businesses: {},
      permissions: []
    });
    setMappings([
      {
        businessName: "",
        meetingTypes: [...meetingTypes]
      }
    ]);
    setEmailLookupText("");
    setBusinessError("");
    form.reset({
      userName: "",
      email: "",
      firstName: "",
      lastName: "",
      roleId: ""
    });
  };

  const openEdit = (user: UserResponse) => {
    setEditing(user);
    const preloadedFromMap: BusinessMeetingTypeMappingRow[] = Object.entries(user.businesses ?? {})
      .map(([businessName, meetingTypeRows]) => ({
        businessName,
        meetingTypes: (meetingTypeRows ?? [])
          .map((m) => m.meetingTypeName)
          .filter((name) => meetingTypes.includes(name))
      }))
      .filter((row) => businesses.includes(row.businessName));

    const csvBusinesses = (user.businessNameCSV ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((name) => name.length > 0 && businesses.includes(name));
    const csvMeetingTypes = (user.meetingTypesNameCSV ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((name) => name.length > 0 && meetingTypes.includes(name));
    const preloadedFromCsv: BusinessMeetingTypeMappingRow[] = csvBusinesses.map((businessName) => ({
      businessName,
      meetingTypes: csvMeetingTypes
    }));

    const preloadedMappings = preloadedFromMap.length > 0 ? preloadedFromMap : preloadedFromCsv;

    setMappings(
      preloadedMappings.length > 0
        ? preloadedMappings
        : [
            {
              businessName: "",
              meetingTypes: [...meetingTypes]
            }
          ]
    );
    setEmailLookupText(user.email ?? "");
    setBusinessError("");
    form.reset({
      userName: user.userName ?? "",
      email: user.email ?? "",
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      roleId: user.roleId?.toString() ?? ""
    });
  };

  const closeModal = () => {
    setEditing(null);
    setMappings([]);
    setEmailLookupText("");
    setBusinessError("");
    form.reset({
      userName: "",
      email: "",
      firstName: "",
      lastName: "",
      roleId: ""
    });
  };

  const onSubmit = form.handleSubmit(async (values) => {
    // Duplicate email check on create — mirrors Blazor:
    // if (Users.Any(u => u.Email?.EqualsExt(CreateRequest.Email)))
    const isCreate = !editing?.id || editing.id === 0;
    if (isCreate) {
      const duplicate = (users ?? []).some(
        (u) => u.email?.toLowerCase() === values.email.toLowerCase()
      );
      if (duplicate) {
        form.setError("email", { message: "A user with this email already exists." });
        return;
      }
    }

    // Business/meeting-type validation only when role requires it
    if (showBusinessSection) {
      if (mappings.length === 0) {
        setBusinessError("At least one business-to-meeting-type mapping is required.");
        return;
      }

      const emptyBusiness = mappings.some((m) => !m.businessName);
      if (emptyBusiness) {
        setBusinessError("Each mapping row must have a business selected.");
        return;
      }

      const emptyMeetingTypes = mappings.some((m) => m.meetingTypes.length === 0);
      if (emptyMeetingTypes) {
        setBusinessError("Select at least one meeting type in each mapping row.");
        return;
      }

      const uniqueBusinesses = new Set(mappings.map((m) => m.businessName));
      if (uniqueBusinesses.size !== mappings.length) {
        setBusinessError("Each business can be selected only once.");
        return;
      }
    }

    const roleId = parseInt(values.roleId, 10);
    const role = roles.find((r) => r.id === roleId);
    if (!role) {
      form.setError("roleId", { message: "Invalid role" });
      return;
    }

    // Transform selected businesses to API format (only when role requires it)
    const businessMap = showBusinessSection
      ? mappings.reduce(
          (acc, row) => {
            acc[row.businessName] = row.meetingTypes.map((meetingTypeName) => ({
              meetingTypeName
            }));
            return acc;
          },
          {} as Record<string, { meetingTypeName: string }[]>
        )
      : {};

    try {
      if (editing?.id && editing.id > 0) {
        await updateMutation.mutateAsync({
          id: editing.id,
          email: values.email,
          userName: values.userName,
          firstName: values.firstName,
          lastName: values.lastName,
          roleId: roleId,
          roleName: role.role,
          userBusinessMeetingTypeMap: showBusinessSection
            ? mappings.reduce(
                (acc, row) => {
                  acc[row.businessName] = row.meetingTypes.map((meetingTypeName) => ({
                    meetingTypeName,
                    isMapped: true
                  }));
                  return acc;
                },
                {} as Record<string, { meetingTypeName: string; isMapped: boolean }[]>
              )
            : {}
        });
        toast.success("User updated successfully.");
      } else {
        await createMutation.mutateAsync({
          email: values.email,
          userName: values.userName,
          firstName: values.firstName,
          lastName: values.lastName,
          roleId: roleId,
          roleName: role.role,
          userBusinessMeetigtypeMaps: businessMap as Record<string, { meetingTypeName: string }[]>
        });
        toast.success("User created successfully.");
      }

      await queryClient.invalidateQueries({ queryKey: settingsKeys.users() });
      closeModal();
    } catch {
      toast.error("Unable to save user.");
    }
  });

  const onDelete = async (user: UserResponse) => {
    if (!user.email) {
      toast.error("User email is missing.");
      return;
    }

    if (user.isLoggedInUser) {
      toast.error("You cannot remove the logged-in user.");
      return;
    }

    const confirmed = window.confirm(`Delete user \"${user.userName ?? user.email}\"?`);
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(user.email);
      toast.success("User removed successfully.");
      await queryClient.invalidateQueries({ queryKey: settingsKeys.users() });
    } catch {
      toast.error("Unable to remove user.");
    }
  };

  return (
    <section className="rounded border-slate-200 bg-white" >
      <div className="mb-4 flex flex-wrap items-center gap-3" >
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
          className="w-full max-w-md border border-slate-300 px-3 py-2 text-sm"
          aria-label="Search users"
        />

        <button
          type="button"
          onClick={openCreate}
          
          
          className="px-3 py-2 text-sm font-medium text-white bg-[#26890d] hover:bg-[#1f6609] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add User
        </button>
      </div>

      {isPending ? (
        <div className="py-8 text-sm text-slate-600">Loading users...</div>
      ) : (
        <div className=" overflow-x-auto overflow-y-auto" style={{ height: "calc(90vh - 150px)" }}>
          <table className="min-w-full border border-slate-200 text-left text-sm" >
            <thead className="sticky top-0 bg-[#E8E8E8] text-xs font-semibold text-black">
              <tr>
                <th className="border-b border-slate-200 px-3 py-2">User Name</th>
                <th className="border-b border-slate-200 px-3 py-2">Email Address</th>
                <th className="border-b border-slate-200 px-3 py-2">User Role</th>
                <th className="border-b border-slate-200 px-3 py-2">Business</th>
                <th className="border-b border-slate-200 px-3 py-2">Meeting Type(s)</th>
                <th className="border-b border-slate-200 px-3 py-2">Date Added</th>
                <th className="border-b border-slate-200 px-3 py-2">Last Login</th>
                <th className="border-b border-slate-200 px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, idx) => (
                <tr key={user.id} className={idx % 2 === 0 ? "bg-white" : "bg-[#F2F2F2]"}>
                  <td className="border-b border-slate-200 px-3 py-2">{user.userName ?? "-"}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{user.email ?? "-"}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{user.roleName ?? "-"}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{user.businessNameCSV || "-"}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{user.meetingTypesNameCSV || "-"}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{displayDate(user.createdDate)}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{displayDate(user.lastLoginDate)}</td>
                  <td className="border-b border-slate-200 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                       
                        onClick={() => openEdit(user)}
                        
                        
                        className="inline-flex h-8 w-8 items-center justify-center border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`Edit ${user.userName ?? user.email ?? "user"}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        
                        onClick={() => onDelete(user)}
                        
                        
                        className="inline-flex h-8 w-8 items-center justify-center border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`Delete ${user.userName ?? user.email ?? "user"}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 ? (
                <tr>
                  <td className="px-3 py-8 text-center text-slate-500" colSpan={8}>
                    No users available.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {isFetching && !isPending ? <div className="mt-2 text-xs text-slate-500">Refreshing users...</div> : null}

      {editing ? (
        <div 
          className="fixed inset-0 z-50 flex bg-black/30" 
          role="dialog" 
          aria-modal="true" 
          aria-labelledby="user-modal-title"
          onClick={closeModal}
          onKeyDown={(e) => { if (e.key === "Escape") closeModal(); }}
        >
          <div 
            className="ml-auto flex w-96 flex-col border-l border-slate-300 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <h3 id="user-modal-title" className="text-base font-semibold text-slate-900">
                {editing.id > 0 ? "Edit User" : "Add User"}
              </h3>
              <button type="button" onClick={closeModal} className="text-slate-500 hover:text-slate-900" aria-label="Close dialog">
                <X className="h-4 w-4" />
              </button>
            </div>

            <form className="flex-1 space-y-3 overflow-y-auto p-4" id="user-form" onSubmit={onSubmit}>
              <div>
                <label htmlFor="user-email" className="mb-1 block text-sm font-medium text-slate-800">
                  Email Lookup
                </label>
                {editing.id > 0 ? (
                  <input
                    id="user-email"
                    type="email"
                    readOnly
                    className={cn(
                      "w-full border border-slate-300 px-3 py-2 text-sm bg-slate-50 text-slate-500",
                      form.formState.errors.email && "border-red-500"
                    )}
                    {...form.register("email")}
                  />
                ) : (
                  <EmailLookupAutocomplete
                    id="user-email"
                    value={emailLookupText}
                    suggestions={profileSuggestions ?? []}
                    isLoading={searchingProfiles}
                    error={form.formState.errors.email?.message}
                    onInputChange={(value) => {
                      setEmailLookupText(value);
                      form.setValue("email", value, { shouldDirty: true, shouldValidate: true });
                      const derivedUserName = value.includes("@") ? value.split("@")[0] : value;
                      form.setValue("userName", derivedUserName, { shouldDirty: true, shouldValidate: true });
                    }}
                    onSelect={(profile) => {
                      setEmailLookupText(profile.email);
                      form.setValue("email", profile.email, { shouldDirty: true, shouldValidate: true });
                      form.setValue("userName", profile.fullName, { shouldDirty: true, shouldValidate: true });
                      form.setValue("firstName", profile.firstName ?? "", { shouldDirty: true });
                      form.setValue("lastName", profile.lastName ?? "", { shouldDirty: true });
                    }}
                  />
                )}
                {form.formState.errors.email ? (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.email.message}</p>
                ) : null}
              </div>

              <div>
                <label htmlFor="user-role" className="mb-1 block text-sm font-medium text-slate-800">
                  Role
                </label>
                <select
                  id="user-role"
                  className={cn("w-full border border-slate-300 px-3 py-2 text-sm", form.formState.errors.roleId && "border-red-500")}
                  {...form.register("roleId", {
                    onChange: () => {
                      setBusinessError("");
                    }
                  })}
                >
                  <option value="0">Select role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.role}
                    </option>
                  ))}
                </select>
                {form.formState.errors.roleId ? (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.roleId.message}</p>
                ) : null}
              </div>

              {showBusinessSection && (
                <BusinessMeetingTypeSelector
                  mappings={mappings}
                  onMappingsChange={(items) => {
                    setMappings(items);
                    setBusinessError("");
                  }}
                  businesses={businesses}
                  meetingTypes={meetingTypes}
                  error={businessError}
                />
              )}

              {editing.id > 0 && (
                <>
                  <div className="grid gap-3">
                    <div>
                      <label htmlFor="user-name" className="mb-1 block text-sm font-medium text-slate-800">
                        User Name
                      </label>
                      <input
                        id="user-name"
                        type="text"
                        className={cn("w-full border border-slate-300 px-3 py-2 text-sm", form.formState.errors.userName && "border-red-500")}
                        {...form.register("userName")}
                      />
                      {form.formState.errors.userName ? (
                        <p className="mt-1 text-xs text-red-600">{form.formState.errors.userName.message}</p>
                      ) : null}
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label htmlFor="user-first-name" className="mb-1 block text-sm font-medium text-slate-800">
                        First Name
                      </label>
                      <input id="user-first-name" type="text" className="w-full border border-slate-300 px-3 py-2 text-sm" {...form.register("firstName")} />
                    </div>

                    <div>
                      <label htmlFor="user-last-name" className="mb-1 block text-sm font-medium text-slate-800">
                        Last Name
                      </label>
                      <input id="user-last-name" type="text" className="w-full border border-slate-300 px-3 py-2 text-sm" {...form.register("lastName")} />
                    </div>
                  </div>
                </>
              )}
            </form>

            <div className="border-t border-slate-200 px-4 py-3">
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeModal} className="border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  form="user-form"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="inline-flex items-center gap-2 border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
                >
                  {createMutation.isPending || updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
